/**
 * Subject Workspace arrival trail — navigation history, not ontology.
 * Stored in sessionStorage so a refresh keeps the hops for this tab only.
 */

import { SUBJECT_RECENTS_STORAGE_KEY, SUBJECT_TRAIL_STORAGE_KEY } from "@l30/shared";

const STORAGE_KEY = SUBJECT_TRAIL_STORAGE_KEY;
const RECENT_KEY = SUBJECT_RECENTS_STORAGE_KEY;
const MAX_TRAIL = 8;
const MAX_RECENTS = 12;

export type SubjectTrailHop = {
  subjectId: string;
  label: string;
  /** Edge `rel` of the hop that led *to* the next subject, when known. */
  rel?: string;
};

function readJson<T>(key: string, fallback: T): T {
  if (typeof sessionStorage === "undefined" && typeof localStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = key === STORAGE_KEY ? sessionStorage.getItem(key) : localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown, session: boolean): void {
  try {
    const store = session ? sessionStorage : localStorage;
    store.setItem(key, JSON.stringify(value));
  } catch {
    // Quota / private mode — trail is optional.
  }
}

export function readSubjectTrail(): SubjectTrailHop[] {
  const hops = readJson<SubjectTrailHop[]>(STORAGE_KEY, []);
  return Array.isArray(hops) ? hops.slice(-MAX_TRAIL) : [];
}

export function replaceSubjectTrail(hops: SubjectTrailHop[]): void {
  writeJson(STORAGE_KEY, hops.slice(-MAX_TRAIL), true);
}

/** Reset trail when arriving from Home / command palette (not a related-subject hop). */
export function startSubjectTrail(): void {
  replaceSubjectTrail([]);
}

/**
 * Record the subject we are leaving, then the caller navigates to the related id.
 */
export function pushSubjectTrailHop(hop: SubjectTrailHop): void {
  const current = readSubjectTrail().filter((h) => h.subjectId !== hop.subjectId);
  replaceSubjectTrail([...current, hop]);
}

export function readRecentSubjectIds(): string[] {
  const ids = readJson<string[]>(RECENT_KEY, []);
  return Array.isArray(ids) ? ids.filter((id) => typeof id === "string") : [];
}

export function recordRecentSubject(subjectId: string): void {
  const next = [subjectId, ...readRecentSubjectIds().filter((id) => id !== subjectId)].slice(
    0,
    MAX_RECENTS,
  );
  writeJson(RECENT_KEY, next, false);
}

/** Drop trail and recents whose IDs belong to a previous tenant. */
export function clearSubjectNavigationSession(): void {
  replaceSubjectTrail([]);
  writeJson(RECENT_KEY, [], false);
}
